"use client";

import { useState } from "react";
import { useMutation } from "convex/react";
import { api } from "@convex/_generated/api";
import { Card, CardContent } from "@/components/ui/card";
import { Clock, Timer, ChevronRight } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { cn } from "@/lib/utils";
import { formatHoursCompact } from "@/lib/format";
import { RollingHoursSheet } from "./rolling-hours-sheet";
import { useAnimatedValue } from "@/hooks/use-animated-value";

interface CapacityMeterProps {
  data: {
    hoursWorkedToday: number;
    dailyLimit: number;
    hoursLeftToday: number;
    hoursAvailableToday: number;
    hoursWorkedLast7Days: number;
    weeklyLimit: number;
    hoursAvailable: number;
    hoursReleasingTomorrow: number;
    weeklyEarnings: number;
    isExtendedHours: boolean;
    // New fields for true rolling
    bottleneck: "daily" | "weekly" | "none";
    nextReleaseTime: number | null;
    nextReleaseHours: number;
  } | null;
}

// Simple gradient from green -> yellow -> red based on fill percentage
function getGradientColor(percent: number): string {
  // 0% = green, 50% = yellow, 100% = red
  if (percent <= 50) {
    // Green to yellow
    const ratio = percent / 50;
    const r = Math.round(16 + (245 - 16) * ratio);
    const g = Math.round(185 + (158 - 185) * ratio);
    const b = Math.round(129 + (11 - 129) * ratio);
    return `rgb(${r}, ${g}, ${b})`;
  } else {
    // Yellow to red
    const ratio = (percent - 50) / 50;
    const r = Math.round(245 + (239 - 245) * ratio);
    const g = Math.round(158 + (68 - 158) * ratio);
    const b = Math.round(11 + (68 - 11) * ratio);
    return `rgb(${r}, ${g}, ${b})`;
  }
}

function ProgressBar({ value }: { value: number }) {
  return (
    <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
      <div
        className="h-full transition-all rounded-full"
        style={{
          width: `${Math.min(value, 100)}%`,
          backgroundColor: getGradientColor(value),
        }}
      />
    </div>
  );
}

// Progress bar showing usage with available portion greyed out
// Layout: [worked (gradient) | blocked (gradient) | available (grey on right)]
function DailyProgressBar({
  workedPercent,
  blockedPercent,
  availablePercent,
}: {
  workedPercent: number;
  blockedPercent: number;
  availablePercent: number;
}) {
  // Total "used" capacity = worked + blocked (determines gradient color)
  const totalUsedPercent = workedPercent + blockedPercent;

  return (
    <div className="h-2 w-full overflow-hidden rounded-full bg-muted relative">
      {/* Worked + Blocked hours with gradient (left side) */}
      {totalUsedPercent > 0 && (
        <div
          className="h-full transition-all absolute left-0 rounded-l-full"
          style={{
            width: `${totalUsedPercent}%`,
            backgroundColor: getGradientColor(totalUsedPercent),
          }}
        />
      )}
      {/* Available hours greyed out (right side) */}
      {availablePercent > 0 && (
        <div
          className="h-full transition-all bg-muted-foreground/40 absolute right-0 rounded-r-full"
          style={{ width: `${availablePercent}%` }}
        />
      )}
    </div>
  );
}

export function CapacityMeter({ data }: CapacityMeterProps) {
  const updateSettings = useMutation(api.settings.updateSettings);
  const [isToggling, setIsToggling] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);

  // Animated values for smooth transitions
  const animatedHoursLeftToday = useAnimatedValue(data?.hoursLeftToday ?? 0);
  const animatedHoursWorkedToday = useAnimatedValue(data?.hoursWorkedToday ?? 0);
  const animatedHoursAvailable = useAnimatedValue(data?.hoursAvailable ?? 0);
  const animatedHoursWorked7Days = useAnimatedValue(data?.hoursWorkedLast7Days ?? 0);

  const handleToggleExtendedHours = async (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent opening sheet when toggling
    if (!data || isToggling) return;
    setIsToggling(true);
    try {
      const newExtendedHours = !data.isExtendedHours;
      await updateSettings({
        extendedHours: newExtendedHours,
        weeklyHourLimit: newExtendedHours ? 50 : 40,
        dailyHourLimit: newExtendedHours ? 10 : 8,
      });
    } finally {
      setIsToggling(false);
    }
  };

  if (!data) {
    return (
      <Card>
        <CardContent className="p-4">
          <div className="h-48 bg-muted animate-pulse rounded" />
        </CardContent>
      </Card>
    );
  }

  const weeklyProgress = (animatedHoursWorked7Days / data.weeklyLimit) * 100;

  // Daily: worked hours as percentage of daily limit
  const dailyWorkedPercent = (animatedHoursWorkedToday / data.dailyLimit) * 100;
  // Blocked = portion of daily limit you can't use due to weekly cap
  const dailyBlockedPercent = Math.max(0, ((data.dailyLimit - animatedHoursWorkedToday - animatedHoursLeftToday) / data.dailyLimit) * 100);
  // Available = what's left to work today
  const dailyAvailablePercent = (animatedHoursLeftToday / data.dailyLimit) * 100;

  // Format next release time
  const nextReleaseText = data.nextReleaseTime
    ? `+${data.nextReleaseHours.toFixed(1)}h ${formatDistanceToNow(data.nextReleaseTime, { addSuffix: true })}`
    : null;

  return (
    <>
    <Card
      className="cursor-pointer hover:bg-accent/50 transition-colors"
      onClick={() => setSheetOpen(true)}
    >
      <CardContent className="p-4 space-y-4">
        {/* Header with toggle */}
        <div className="flex items-center justify-between">
          <h3 className="font-semibold flex items-center gap-2">
            <Clock className="w-4 h-4" />
            Hours Available
          </h3>
          <div className="flex items-center gap-2">
            <button
              onClick={handleToggleExtendedHours}
              disabled={isToggling}
              className={`text-xs px-2 py-0.5 rounded-full font-medium transition-colors ${
                data.isExtendedHours
                  ? "bg-amber-500/10 text-amber-600"
                  : "bg-muted text-muted-foreground"
              } ${isToggling ? "opacity-50" : "hover:opacity-80"}`}
            >
              {isToggling ? "..." : data.isExtendedHours ? "Extended" : "Regular"}
            </button>
            <ChevronRight className="w-4 h-4 text-muted-foreground" />
          </div>
        </div>

        {/* Bottleneck indicator */}
        {data.bottleneck !== "none" && (
          <div className="flex items-center justify-center gap-2 text-sm">
            <span className={cn(
              "px-2 py-0.5 rounded-full text-xs font-medium",
              data.bottleneck === "daily"
                ? "bg-amber-500/20 text-amber-600 dark:text-amber-400"
                : "bg-blue-500/20 text-blue-600 dark:text-blue-400"
            )}>
              {data.bottleneck === "daily" ? "Daily limit" : "7-day limit"}
            </span>
            {nextReleaseText && data.bottleneck === "weekly" && (
              <span className="text-muted-foreground text-xs">
                {nextReleaseText}
              </span>
            )}
          </div>
        )}

        {/* Today's Hours (resets at midnight) */}
        <div className="space-y-1.5">
          <div className="flex justify-between text-sm">
            <span className={cn(
              data.bottleneck === "daily" ? "font-medium" : "text-muted-foreground"
            )}>
              Today
            </span>
            <span className="font-medium">{formatHoursCompact(animatedHoursLeftToday)} left</span>
          </div>
          <DailyProgressBar
            workedPercent={dailyWorkedPercent}
            blockedPercent={dailyBlockedPercent}
            availablePercent={dailyAvailablePercent}
          />
          <p className="text-xs text-muted-foreground">
            {formatHoursCompact(animatedHoursWorkedToday)} worked · {formatHoursCompact(animatedHoursLeftToday)}/{data.dailyLimit}h available
          </p>
        </div>

        {/* Rolling 7 days Hours */}
        <div className="space-y-1.5">
          <div className="flex justify-between text-sm">
            <span className={cn(
              data.bottleneck === "weekly" ? "font-medium" : "text-muted-foreground"
            )}>
              Rolling 7 days
            </span>
            <span className="font-medium">{formatHoursCompact(animatedHoursAvailable)} available</span>
          </div>
          <ProgressBar value={weeklyProgress} />
          <div className="flex justify-between text-xs text-muted-foreground">
            <span>{formatHoursCompact(animatedHoursWorked7Days)} worked · {data.weeklyLimit}h limit</span>
            {data.hoursReleasingTomorrow > 0 && (
              <span className="text-emerald-600 dark:text-emerald-400">
                +{formatHoursCompact(data.hoursReleasingTomorrow)} tomorrow
              </span>
            )}
          </div>
        </div>

        {/* Bottom stats row */}
        <div className="flex items-center justify-center pt-2 border-t text-sm">
          {nextReleaseText && data.bottleneck === "weekly" ? (
            <div className="flex items-center gap-1.5 text-muted-foreground">
              <Timer className="w-3.5 h-3.5" />
              <span>{nextReleaseText}</span>
            </div>
          ) : data.bottleneck === "daily" ? (
            <div className="flex items-center gap-1.5 text-muted-foreground">
              <Timer className="w-3.5 h-3.5" />
              <span className="text-xs">Resets at midnight</span>
            </div>
          ) : (
            <div className="flex items-center gap-1.5 text-muted-foreground">
              <span className="text-xs">All capacity available</span>
            </div>
          )}
        </div>
      </CardContent>
    </Card>

    <RollingHoursSheet
      open={sheetOpen}
      onOpenChange={setSheetOpen}
      weeklyLimit={data.weeklyLimit}
    />
    </>
  );
}
